import type { DevicePorts } from "../runtime/ports";

const MAX_DIAGNOSTIC_KEYS = 20;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getObjectKeys = (value: unknown) =>
  isRecord(value) ? Object.keys(value).slice(0, MAX_DIAGNOSTIC_KEYS) : [];

const getErrorDiagnostics = (error: unknown) => ({
  errorMessage:
    error instanceof Error && error.message ? error.message : String(error),
  errorName: error instanceof Error ? error.name : typeof error,
});

const getQueueDiagnostics = (queue: unknown) => {
  if (!isRecord(queue)) {
    return {
      currentIndex: null,
      currentSongId: null,
      hasCurrentItem: false,
      itemCount: 0,
    };
  }

  const items = Array.isArray(queue.items) ? queue.items : [];
  const currentIndex =
    typeof queue.currentIndex === "number" ? queue.currentIndex : null;
  const currentItem =
    currentIndex !== null && currentIndex >= 0 ? items[currentIndex] : null;
  const metadata = isRecord(currentItem) ? currentItem.metadata : null;

  return {
    currentIndex,
    currentSongId:
      isRecord(metadata) && typeof metadata.id === "string"
        ? metadata.id
        : null,
    hasCurrentItem: Boolean(currentItem),
    itemCount: items.length,
  };
};

export const getZustandPersistStorageDiagnostics = (
  name: string,
  storage: Pick<DevicePorts["kv"], "getString">,
) => {
  const base = {
    key: name,
    parseOk: null as boolean | null,
    rawExists: null as boolean | null,
    rawLength: null as number | null,
    readOk: false,
  };

  let rawValue: string | undefined;
  try {
    rawValue = storage.getString(name);
  } catch (error) {
    return {
      ...base,
      ...getErrorDiagnostics(error),
    };
  }

  if (rawValue === undefined) {
    return {
      ...base,
      parseOk: null,
      rawExists: false,
      readOk: true,
    };
  }

  const withRaw = {
    ...base,
    rawExists: true,
    rawLength: rawValue.length,
    readOk: true,
  };

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawValue);
  } catch (error) {
    return {
      ...withRaw,
      parseOk: false,
      ...getErrorDiagnostics(error),
    };
  }

  const state = isRecord(parsed) ? parsed.state : undefined;
  const stateRecord = isRecord(state) ? state : {};

  return {
    ...withRaw,
    parseOk: true,
    persistedActiveQueueType:
      typeof stateRecord.activeQueueType === "string"
        ? stateRecord.activeQueueType
        : null,
    persistedHasHydrated:
      typeof stateRecord._hasHydrated === "boolean"
        ? stateRecord._hasHydrated
        : null,
    persistedForyouQueue: getQueueDiagnostics(stateRecord.foryouQueue),
    persistedPlaylistQueue: getQueueDiagnostics(stateRecord.playlistQueue),
    persistedPlaylistRecommendationCount: Array.isArray(
      stateRecord.playlistRecommendationItems,
    )
      ? stateRecord.playlistRecommendationItems.length
      : 0,
    stateKeys: getObjectKeys(state),
    topLevelKeys: getObjectKeys(parsed),
    version:
      isRecord(parsed) && typeof parsed.version === "number"
        ? parsed.version
        : null,
  };
};
